/** @format */

import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { useForm } from "react-hook-form";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import { TAXI_QUOTE_BRANDREF } from "../../../config/config";
import { useEffect, useRef, useState } from "react";
import Switch from "react-switch";
import BuyStep from "../enums/BuyStep";
import QuoteStep from "../../quote/enums/QuoteStep";
import useQuoteStore from "../../quote/store/QuoteStore";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { useNavigate } from "fusion-plugin-react-router";
import {
  fetchTemplateList,
  getAddonDocumentDownloadURL,
  saveMember,
} from "../services/buyService";
import useBuyStore from "../store/BuyStore";
import Loader from "../../../shared/components/Loader";
import { handleDownloadPDF } from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useAppStateStore from "../../../shared/store/AppStateStore";

const TbAddonRecommendations = () => {
  // Form Setup
  const {
    formState: { errors, isValid },
    setValue,
    handleSubmit,
  } = useForm();

  let STATE = useAppStateStore();

  const navigate = useNavigate();
  const buyStore = useBuyStore.getState();
  const addonFields = buyStore.getObject("addonFields") || {};
  const recommendationsData = buyStore.getObject("recommendationsData");
  const addonsDescriptions = buyStore.getObject("addonDescriptions");

  const [selectedAddons, setSelectedAddons] = useState(
    new Set(buyStore.getObject("selectedAddons") || [])
  );

  // Constants
  const css = assetUrl("../../../styles/main.css");
  const Group = assetUrl("../../../assets/icons/Group.svg");
  const cardCss = assetUrl("../../../styles/tqPanelCard.css");
  const brandRef = TAXI_QUOTE_BRANDREF;
  const riskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;
  const previousRecommendationsData = useRef(null);
  const selectedSchemeTableId = STATE.selectedSchemeTableId;

  // Initialize form data
  useEffect(() => {
    const storedAddons = buyStore.getObject("selectedAddons");
    if (storedAddons) {
      setSelectedAddons(new Set(storedAddons));
      setValue("addedAddons", new Set(storedAddons));
      setValue("recommendationsAddedAddons", storedAddons);
    }
  }, []);

  const toggleAddon = (addonId, addonIPIDTriggerUrl) => {
    const newAddedAddons = new Set(selectedAddons);
    if (newAddedAddons.has(addonId)) {
      newAddedAddons.delete(addonId);
    } else {
      newAddedAddons.add(addonId);
      if (addonIPIDTriggerUrl) {
        getAddonDocumentDownloadURL(addonIPIDTriggerUrl, navigate);
      }
    }
    setSelectedAddons(newAddedAddons);
    buyStore.setObject("selectedAddons", Array.from(newAddedAddons));
    setValue("addedAddons", newAddedAddons);
    setValue("recommendationsAddedAddons", Array.from(newAddedAddons));
    setValue("selectedOption", undefined);
  };

  // Update useEffect to use selectedAddons instead of savedAddedAddons
  useEffect(() => {
    const hasDataChanged =
      JSON.stringify(previousRecommendationsData.current) !==
      JSON.stringify(recommendationsData);

    const vaultRequestBody = {
      BrandRef: brandRef,
      riskObjectId: riskObjectId,
      riskObjectHistoryId: 1,
      documentTagStage: "Quote",
      documentTagCategory: "PreSaleQuoteDocuments",
    };

    buyStore.setObject("vaultRequestBody", vaultRequestBody);

    // Get the array of selected addon IDs
    const selectedAddonArray = Array.from(selectedAddons);

    const saveMemberRequestBody = {
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: "1",
      BrandRef: brandRef,
      PreSelectedQuoteAddons: selectedAddonArray,
      SelectedSchemeTableId: selectedSchemeTableId || "",
      UseSessionData: hasDataChanged,
    };
    buyStore.setObject("saveMemberRequestBody", saveMemberRequestBody);

    previousRecommendationsData.current = recommendationsData;
  }, [
    selectedAddons,
    recommendationsData,
    riskObjectId,
    selectedSchemeTableId,
    brandRef,
  ]);

  const [loadingStates, setLoadingStates] = useState({});

  const handleDownloadClick = async (e, link, name, index) => {
    e.stopPropagation();
    setLoadingStates((prev) => ({ ...prev, [index]: true }));
    try {
      await handleDownloadPDF(navigate, link, name);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [index]: false }));
    }
  };

  useEffect(() => {
    TrackHotjarEvent(
      HotjarEventNames.TAXI_QUOTE_TBADDONRECOMMENDATIONNS_ENTERED
    );
  }, []);

  const renderAddons = () => {
    if (!recommendationsData) return null;

    return recommendationsData.map((addon, index) => {
      const isSelected = selectedAddons.has(addon.productAddonId);
      const monthlyPremium = (addon.addonGrossPremiumIncIPT / 12).toFixed(2);
      const annualPremium = addon.addonGrossPremiumIncIPT.toFixed(2);
      const isLoading = loadingStates[index] || false;

      const addonDescription =
        addonsDescriptions?.find(
          (desc) => desc.addonId === addon.productAddonId
        )?.description || "";

      return (
        <div
          className={`form4 ${isSelected ? "selected-recommendation" : ""}`}
          key={addon.productAddonId}
        >
          <div className="flex justify-between items-center">
            <h5 className="lh-0">{addon.addonSchemeName}</h5>
            <Switch
              width={50}
              height={30}
              handleDiameter={26}
              offColor="#e8e8e8"
              onColor="#175bcc"
              checkedIcon={false}
              uncheckedIcon={false}
              checked={isSelected}
              onChange={() =>
                toggleAddon(addon.productAddonId, addon.addonIPIDTriggerUrl)
              }
            />
          </div>

          <p className="margin-top-10 gray-text">{addon.supplier}</p>
          <p>{addonDescription}</p>

          <div className="flex addon400px gap-4 margin-bottom-20">
            <div className="recommendations-per-year-class">
              <img className="" src={Group} alt="" />
              <p className="navy-text">
                <span className="bold">Annual:</span> £{annualPremium}
              </p>
            </div>
            <div className="recommendations-per-year-class">
              <img className="" src={Group} alt="" />
              <p className="navy-text">
                <span className="bold">Monthly:</span> £{monthlyPremium}
              </p>
            </div>
          </div>
          <div className="recommendationButtonsDiv addon400px">
            <button
              type="button"
              className="AddButton bg-E8E8E8"
              disabled={isLoading}
              key={index}
              onClick={(e) =>
                handleDownloadClick(
                  e,
                  addon.policyWordingGetUrl,
                  addon.policyWordingDownloadFileName,
                  index
                )
              }
            >
              {isLoading ? (
                <div className="pr-20">
                  <div className="loader"></div>
                </div>
              ) : (
                "Policy Wording"
              )}
            </button>
          </div>
        </div>
      );
    });
  };
  const onSubmit = async () => {
    STATE.setDocumentsLoading(true);
    TrackHotjarEvent(
      HotjarEventNames.TAXI_QUOTE_TBADDONRECOMMENDATIONNS_NEXT_CLICKED
    );
    try {
      await saveMember(buyStore.getObject("saveMemberRequestBody"));
      // handleNext(BuyStep.TB_DOCUMENTS, STATE);

      const response = await fetchTemplateList(
        buyStore.getObject("vaultRequestBody"),
        navigate
      );
      buyStore.setObject("vaultResponse", response);
    } catch (error) {
      console.error("Error in addon recommendations submission:", error);
    } finally {
      STATE.setDocumentsLoading(false);
    }
    handleNext(BuyStep.TB_DOCUMENTS, STATE);
  };

  if (STATE.documentsLoading) {
    return <Loader text="We are just fetching your documentation." />;
  }

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <link rel="stylesheet" href={css} />
        <link rel="stylesheet" href={cardCss} />
      </Helmet>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <div className="margin-bottom-5">
            <h1 className="margin-bottom-10">
              {recommendationsData?.title || "Add-ons available to you"}
            </h1>
            <p className="margin-bottom-30">
              All add-ons include insurance premium tax and the monthly price is
              equivalent to the annual cost divided by 12 months excluding
              interest.
            </p>
            {STATE.recommendationsLoading ? (
              <div className="loader"></div>
            ) : recommendationsData?.length > 0 ? (
              <div>
                {renderAddons()}
                <p className="margin-bottom-15">
                  *These examples are provided for illustrative purposes only.
                  Please refer to the policy wording for terms and conditions.
                </p>
              </div>
            ) : (
              <p>No add-ons available</p>
            )}
          </div>
        </QuoteFormWrapper>

        <FormNavigationButtons prevStep={QuoteStep.TQ_PANEL} isValid={true} />
      </form>
    </>
  );
};

export default TbAddonRecommendations;
